import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { IconStars } from '../icon';

const ChallengeSuccess = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-grow flex-col gap-6', className)}
    {...props}
  />
));
ChallengeSuccess.displayName = 'ChallengeSuccess';

const ChallengeSuccessHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('inline-flex flex-col items-center justify-center rounded-b-2xl border border-neutral-lightest bg-white p-5 px-[max(20px,3vw)] text-center shadow-dropshadow-md', className)}
    {...props}
  />
));
ChallengeSuccessHeader.displayName = 'ChallengeSuccessHeader';

const ChallengeSuccessTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('mb-0 text-base font-bold text-primary', className)}
    {...props}
  />
));
ChallengeSuccessTitle.displayName = 'ChallengeSuccessTitle';

const ChallengeSuccessSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mb-0 flex items-center gap-1 text-sm font-light text-neutral [&_svg]:w-4 [&_svg]:shrink-0', className)}
    {...props}
  />
));
ChallengeSuccessSubtitle.displayName = 'ChallengeSuccessSubtitle';

const ChallengeSuccessBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-grow flex-col items-center justify-center gap-[44px] text-center', className)}
    {...props}
  />
));
ChallengeSuccessBody.displayName = 'ChallengeSuccessBody';

const ChallengeSuccessMessage = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col items-center gap-1 [&>*]:mb-0', className)}
    {...props}
  />
));
ChallengeSuccessMessage.displayName = 'ChallengeSuccessMessage';

const ChallengeSuccessMessageTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn('text-center text-2xl font-bold leading-tight text-primary', className)}
    {...props}
  />
));
ChallengeSuccessMessageTitle.displayName = 'ChallengeSuccessMessageTitle';

const ChallengeSuccessMessageSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-center text-sm font-light text-neutral', className)}
    {...props}
  />
));
ChallengeSuccessMessageSubtitle.displayName = 'ChallengeSuccessMessageSubtitle';

const ChallengeSuccessPoints = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('relative', className)}
    {...props}
  />
));
ChallengeSuccessPoints.displayName = 'ChallengeSuccessPoints';

const ChallengeSuccessTotal = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('absolute left-0 right-0 top-6 mb-0 truncate px-4 text-center text-3xl font-bold leading-tight text-white', className)}
    {...props}
  />
));
ChallengeSuccessTotal.displayName = 'ChallengeSuccessTotal';

const weekChallengeStarsVariants = cva('absolute -right-9 -top-6 text-[#FDB462]', {
  variants: {
    variant: {
      icon: 'relative right-0 top-0'
    }
  },
  defaultVariants: {
    variant: null
  }
});

const ChallengeSuccessStars = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(weekChallengeStarsVariants({ variant }), className)}
    {...props}
  >
    <IconStars />
  </div>
));
ChallengeSuccessStars.displayName = 'ChallengeSuccessStars';

const ChallengeSuccessKPIList = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-wrap justify-center gap-2', className)}
    {...props}
  />
));
ChallengeSuccessKPIList.displayName = 'ChallengeSuccessKPIList';

const ChallengeSuccessKPI = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex w-28 max-w-full flex-col gap-2 rounded-sm border border-solid border-primary bg-white p-1 [&>*]:mb-0', className)}
    {...props}
  />
));
ChallengeSuccessKPI.displayName = 'ChallengeSuccessKPI';

const ChallengeSuccessKPITitle = React.forwardRef(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('flex items-center justify-center gap-0.5 rounded-sm bg-primary p-0.5 px-2 text-center text-xs font-bold text-bright', className)}
    {...props}
  />
));
ChallengeSuccessKPITitle.displayName = 'ChallengeSuccessKPITitle';

const ChallengeSuccessKPIValue = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('flex items-center justify-center gap-2 text-sm font-bold text-neutral-dark', className)}
    {...props}
  />
));
ChallengeSuccessKPIValue.displayName = 'ChallengeSuccessKPIValue';

const ChallengeSuccessKPIIcon = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('flex h-5 w-5 items-center justify-center rounded-full bg-neutral text-white [&_svg]:h-3 [&_svg]:w-3', className)}
    {...props}
  />
));
ChallengeSuccessKPIIcon.displayName = 'ChallengeSuccessKPIIcon';

export {
  ChallengeSuccess,
  ChallengeSuccessHeader,
  ChallengeSuccessTitle,
  ChallengeSuccessSubtitle,
  ChallengeSuccessBody,
  ChallengeSuccessMessage,
  ChallengeSuccessMessageTitle,
  ChallengeSuccessMessageSubtitle,
  ChallengeSuccessPoints,
  ChallengeSuccessTotal,
  ChallengeSuccessStars,
  ChallengeSuccessKPIList,
  ChallengeSuccessKPI,
  ChallengeSuccessKPITitle,
  ChallengeSuccessKPIValue,
  ChallengeSuccessKPIIcon
};
